"use client"
// Copyright Polaris Solutions. SPDX-License-Identifier: Apache-2.0

/**
 * ChatStreamContext: App ルートレベルで Chat 応答ストリームを保持する。
 *
 * 設計理由（2026-06-21）:
 *   従来は ChatInterface のローカル useRef で AbortController を持ち、コンポーネントの
 *   useEffect cleanup でストリームを abort していた。このため /chat → /admin など
 *   別ルートに遷移すると ChatPage ごとアンマウントされ、SSE が強制終了して応答が
 *   途切れていた。
 *
 *   本 Context は AuthProvider 直下に置き、ストリーミング状態（sessionId / partial /
 *   isStreaming / abortController）を全ルート間で保持する。ChatInterface はこの context を
 *   見るだけ。アンマウントしてもストリームは context 上で生き続け、完了時に
 *   onComplete(updateSession API) を叩いて DB 永続化する。
 *
 *   ユーザーが /chat に戻ってきた時:
 *   - ストリーミング中: context.streamingContent をそのまま表示
 *   - ストリーミング完了済み: useSessions が DB から完成形を再ロード
 *
 * 切断検知と自動再開（2026-07-17）:
 *   サーバは健全なら keepalive を3秒毎に必ず流し、正常に閉じるターンの最後に
 *   turn_end マーカーを流す。これを前提に、
 *   - 受信バイト（keepalive 含む）を生存信号として無音45秒で切断と判定する
 *     （旧 600 秒は keepalive を生存信号に数えていなかった時代の値）
 *   - turn_end が無いままストリームが終わったら途中切断と判定する
 *   - 異常切断は「（自動再開）…」の user メッセージで 1 回だけ自動再開する。
 *     サーバ側は AgentCore Memory に途中経過を保存済みなので、モデルが完了済み
 *     なら結果を再提示し、未完了なら続きから再開できる。2 連敗で従来の警告に着地。
 */

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react"
import { Attachment, Message } from "@/components/chat/types"
import { KnowledgeFile } from "@/services/projectService"
import { invokeAgentCore } from "@/services/agentCoreService"

export interface StartStreamRequest {
  userMessage: string
  sessionId: string
  /** ストリーム開始時点の past messages（user / assistant placeholder を含めない素の履歴） */
  previousMessages: Message[]
  /** ストリーム開始時に既に作って画面に表示済みの user message オブジェクト
   *  （context が最終的に previousMessages の後ろに append して DB 保存する） */
  userMessageObject: Message
  accessToken: string
  userId: string
  email?: string
  attachments?: Attachment[]
  customInstructions?: string
  knowledgeFiles?: KnowledgeFile[]
  extendedThinking: boolean
  /** トークン受信ごとに呼ばれる（マウント中の ChatInterface が UI 更新に使う任意フック） */
  onChunk?: (content: string) => void
  /** ストリーム完了時。final messages を DB 保存する。
   *  ChatInterface アンマウント後でも呼ばれる（updateSession API は React state に
   *  依存しない素のサービス呼び出しを想定）。 */
  onComplete: (finalMessages: Message[], sessionId: string) => void | Promise<void>
  /** エラー時。partialContent がある場合は部分結果も渡す。 */
  onError: (errorMessage: string, partialContent: string) => void | Promise<void>
  /** 自動再開の開始時に呼ばれる（2026-07-17）。マウント中の ChatInterface が
   *  再開 user メッセージ＋新しい assistant placeholder を画面に挿入するためのフック。
   *  未指定でも再開自体は行われ、完了時の onComplete に全メッセージが載る。 */
  onResume?: (resumeUserMessage: Message) => void
}

interface ChatStreamContextType {
  /** 現在ストリーミング中のセッション ID（無ければ null） */
  streamingSessionId: string | null
  /** 現在受信中の partial assistant content（無ければ ""） */
  streamingContent: string
  /** ストリーミング中フラグ */
  isStreaming: boolean
  /** コールドスタート等で接続リトライ中に出す案内（無ければ null）。
   *  従来はリトライが UI に一切見えず「考え中…のまま無反応」に見えていた（2026-07-02 P3） */
  connectionNotice: string | null
  /** ツール実行中の表示（例:「Webを検索しています…」・無ければ null）。
   *  ツール実行の数十秒はテキストが流れず無言に見えるため（2026-07-02 P3） */
  activityNotice: string | null
  /** ストリーム開始（context 内で AbortController を保持し、router 遷移で abort しない） */
  startStream: (req: StartStreamRequest) => Promise<void>
  /** 明示的中断（session switch / 手動停止）。abort 後に onComplete で部分結果が DB に保存される。 */
  stopStream: () => void
}

const ChatStreamContext = createContext<ChatStreamContextType | null>(null)

// ツール名 → 実行中表示のラベル。未知のツールは汎用文言で出す（新ツール追加時に
// ここが漏れても壊れない）。
const TOOL_ACTIVITY_LABELS: Record<string, string> = {
  web_search: "Webを検索しています…",
  web_fetch: "Webページを読んでいます…",
  read_uploaded_file: "添付ファイルを読んでいます…",
  create_image: "画像を生成しています…",
  edit_image: "画像を編集しています…",
  inspect_image: "画像を確認しています…",
  compile_manga_pdf: "PDF にまとめています…",
  generate_speech: "音声を生成しています…",
  register_board_task: "Board にタスクを登録しています…",
  register_recurring_task: "定期タスクを登録しています…",
  register_knowledge: "ナレッジを登録しています…",
  list_knowledge: "ナレッジ一覧を確認しています…",
  read_knowledge: "ナレッジを読んでいます…",
  remember_fact: "記憶に書き留めています…",
  record_daily: "今日の歩みを記録しています…",
  read_daily: "日誌を読み返しています…",
  curate_memory: "記憶の手入れを準備しています…",
}

function toolActivityLabel(name: string): string {
  return TOOL_ACTIVITY_LABELS[name] ?? `「${name}」を実行しています…`
}

export function useChatStream(): ChatStreamContextType {
  const ctx = useContext(ChatStreamContext)
  if (!ctx) {
    throw new Error("useChatStream must be used within ChatStreamProvider")
  }
  return ctx
}

// ストリーム開始後の無音しきい値。サーバは健全なら keepalive（3秒毎）を必ず流すので、
// 45 秒の完全無音は接続死と判定してよい（2026-07-17 切断対策）。
const STREAM_SILENCE_TIMEOUT_MS = 45000
// 接続確立前（レスポンスヘッダ受信前）のしきい値。コールドスタートは最大~2分かかり、
// その間はエラーもバイトも来ない fetch 待ちがありうるため長めに取る。
const CONNECT_SILENCE_TIMEOUT_MS = 180000
// 異常切断時に 1 回だけ自動送信する再開メッセージ（通常の user 吹き出しとして履歴に残す）
const RESUME_MESSAGE =
  "（自動再開）通信が途中で切断されました。直前の依頼が完了している場合は結果を簡潔に示し、未完了の場合は続きから再開してください。"

export function ChatStreamProvider({ children }: PropsWithChildren) {
  const [streamingSessionId, setStreamingSessionId] = useState<string | null>(null)
  const [streamingContent, setStreamingContent] = useState("")
  const [isStreaming, setIsStreaming] = useState(false)
  const [connectionNotice, setConnectionNotice] = useState<string | null>(null)
  const [activityNotice, setActivityNotice] = useState<string | null>(null)
  // contentRef は同期的に最新値を保持（state は非同期更新で onComplete 時に古い可能性あり）
  const contentRef = useRef<string>("")
  const abortRef = useRef<AbortController | null>(null)

  const startStream = useCallback(async (req: StartStreamRequest) => {
    // 既存ストリームがあれば中断（明示停止せずに新規 start が来た場合の保険）
    if (abortRef.current) {
      try {
        abortRef.current.abort()
      } catch {
        /* ignore */
      }
    }

    contentRef.current = ""
    setStreamingContent("")
    setStreamingSessionId(req.sessionId)
    setIsStreaming(true)
    setConnectionNotice(null)
    setActivityNotice(null)

    // 確定済みメッセージ列。自動再開時は「部分結果の assistant」と「再開 user」を
    // ここに積み足し、最終的な onComplete / 部分保存はすべてこの列を土台にする。
    const baseMessages: Message[] = [...req.previousMessages, req.userMessageObject]
    let ctrl: AbortController | null = null

    try {
      // attempt 0 = 通常送信、attempt 1 = 自動再開（1回まで）
      for (let attempt = 0; attempt <= 1; attempt++) {
        const thisCtrl = new AbortController()
        ctrl = thisCtrl
        abortRef.current = thisCtrl

        let lastEventAt = Date.now()
        let streamStarted = false
        let watchdogTimedOut = false
        const watchdog = setInterval(() => {
          const limit = streamStarted
            ? STREAM_SILENCE_TIMEOUT_MS
            : CONNECT_SILENCE_TIMEOUT_MS
          if (Date.now() - lastEventAt > limit) {
            watchdogTimedOut = true
            thisCtrl.abort()
          }
        }, 5000)

        let turnEnded = false
        let invokeError: unknown = null
        try {
          const result = await invokeAgentCore(
            attempt === 0 ? req.userMessage : RESUME_MESSAGE,
            req.sessionId,
            (content: string) => {
              lastEventAt = Date.now()
              contentRef.current = content
              setStreamingContent(content)
              setConnectionNotice(null) // 応答が届き始めたら起動中案内を消す
              try {
                req.onChunk?.(content)
              } catch {
                /* onChunk 失敗で本流を止めない */
              }
            },
            req.accessToken,
            req.userId,
            // 添付・ナレッジは元の user メッセージに属する。再開ターンで再送すると
            // 二重注入になるため送らない（モデルは会話履歴側で参照済み）。
            attempt === 0 && req.attachments && req.attachments.length > 0
              ? req.attachments
              : undefined,
            req.customInstructions,
            attempt === 0 ? req.knowledgeFiles : undefined,
            req.extendedThinking,
            thisCtrl.signal,
            req.email,
            (connAttempt: number, maxRetries: number) => {
              lastEventAt = Date.now() // 起動待ちを watchdog の沈黙と誤認させない
              setConnectionNotice(
                `エージェントを起動しています…（休止後の初回は最大2分ほどかかります・接続試行 ${connAttempt}/${maxRetries}）`
              )
            },
            (toolName: string | null) => {
              lastEventAt = Date.now() // ツール実行イベントも生存シグナルとして扱う
              setActivityNotice(toolName ? toolActivityLabel(toolName) : null)
            },
            () => {
              // 受信バイト（keepalive 含む）＝生存信号。ヘッダ受信以降は短い無音
              // しきい値（45秒）に切り替える。
              lastEventAt = Date.now()
              streamStarted = true
            }
          )
          turnEnded = result.turnEnded
        } catch (err) {
          invokeError = err
        } finally {
          clearInterval(watchdog)
        }

        // ── 正常完了（turn_end 受信済み）──
        if (!invokeError && turnEnded) {
          const finalAssistant: Message = {
            role: "assistant",
            content: contentRef.current,
            timestamp: new Date().toISOString(),
          }
          await req.onComplete([...baseMessages, finalAssistant], req.sessionId)
          return
        }

        // ── ユーザーの明示停止（watchdog ではない Abort）: 部分保存して終了・再開しない ──
        const isAbort =
          invokeError instanceof Error && invokeError.name === "AbortError"
        if (isAbort && !watchdogTimedOut) {
          const partial = contentRef.current
          if (partial) {
            const partialAssistant: Message = {
              role: "assistant",
              content: partial,
              timestamp: new Date().toISOString(),
            }
            try {
              await req.onComplete([...baseMessages, partialAssistant], req.sessionId)
            } catch {
              /* DB 保存失敗は本流を止めない */
            }
          }
          return
        }

        // ── 異常切断（network error / 無音タイムアウト / turn_end 欠落）──
        if (attempt === 0) {
          // 自動再開: 受信済み partial を確定列に積み、再開メッセージを user として追加
          const partial = contentRef.current
          if (partial) {
            baseMessages.push({
              role: "assistant",
              content: partial,
              timestamp: new Date().toISOString(),
            })
          }
          const resumeMsg: Message = {
            role: "user",
            content: RESUME_MESSAGE,
            timestamp: new Date().toISOString(),
          }
          baseMessages.push(resumeMsg)
          try {
            req.onResume?.(resumeMsg)
          } catch {
            /* UI 挿入の失敗で本流を止めない */
          }
          contentRef.current = ""
          setStreamingContent("")
          setConnectionNotice("通信が切断されたため、自動で再開しています…")
          setActivityNotice(null)
          continue
        }

        // ── 2 連敗: 従来のエラー着地 ──
        // 2026-07-14: network error 等の異常切断でも部分結果を DB 保存する。従来は
        // この経路で保存されず、サーバ側で処理が完了していてもリロードでターンごと
        // 会話記録から消えていた（NetSuite 見積登録の実事例で検出）。
        // notice は載せず素の内容を保存する（notice は表示中の一時案内・履歴に固定しない）。
        const partial = contentRef.current
        if (partial) {
          const partialAssistant: Message = {
            role: "assistant",
            content: partial,
            timestamp: new Date().toISOString(),
          }
          try {
            await req.onComplete([...baseMessages, partialAssistant], req.sessionId)
          } catch {
            /* DB 保存失敗は本流を止めない */
          }
        }
        const msg = watchdogTimedOut
          ? "応答が一定時間途絶えたため中断しました。お手数ですが、もう一度お試しください。"
          : invokeError instanceof Error
            ? invokeError.message
            : "Unknown error"
        try {
          await req.onError(msg, partial)
        } catch {
          /* ignore */
        }
        return
      }
    } finally {
      // 自世代ガード: 停止直後にすぐ再送信した場合、新 startStream が既に
      // abortRef.current を新しい controller に差し替え、isStreaming/content も
      // 再セットしている。旧 finally がそれらを潰さないよう、状態クリアは
      // 自分が設定した controller のままの時（＝後続ストリーム未開始）だけ行う。
      if (ctrl && abortRef.current === ctrl) {
        setIsStreaming(false)
        setStreamingSessionId(null)
        setStreamingContent("")
        setConnectionNotice(null)
        setActivityNotice(null)
        contentRef.current = ""
        abortRef.current = null
      }
    }
  }, [])

  const stopStream = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  return (
    <ChatStreamContext.Provider
      value={{
        streamingSessionId,
        streamingContent,
        isStreaming,
        connectionNotice,
        activityNotice,
        startStream,
        stopStream,
      }}
    >
      {children}
    </ChatStreamContext.Provider>
  )
}
